import React from 'react';
import { 
  Box, 
  TextField, 
  Typography, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Autocomplete,
  Checkbox,
  Chip
} from '@mui/material';
import { CheckBoxOutlineBlank, CheckBox } from '@mui/icons-material';

interface SelectOption {
  value: any;
  label: string;
}

interface EditableFieldProps {
  icon?: React.ReactNode;
  label: string;
  value?: string | string[] | null;
  editMode: boolean;
  onChange?: (value: any) => void;
  type?: 'text' | 'email' | 'date' | 'number' | 'select' | 'multiselect';
  required?: boolean;
  multiline?: boolean;
  rows?: number;
  options?: SelectOption[];
  displayValue?: string; // For displaying formatted value in read mode
}

const EditableField: React.FC<EditableFieldProps> = ({
  icon,
  label,
  value,
  editMode,
  onChange,
  type = 'text',
  required = false,
  multiline = false,
  rows = 1,
  options = [],
  displayValue,
}) => {
  if (editMode && onChange) {
    // Select dropdown - show even if options are empty (loading state)
    if (type === 'select') {
      return (
        <FormControl fullWidth size="small" required={required}>
          <InputLabel>{label}</InputLabel>
          <Select
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            label={label}
            disabled={options.length === 0}
          >
            <MenuItem value="">
              <em>{options.length === 0 ? 'جاري التحميل...' : '-'}</em>
            </MenuItem>
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    // Multi-select dropdown with Autocomplete - show even if options are empty
    if (type === 'multiselect') {
      const selectedValues = Array.isArray(value) ? value : (value ? [value] : []);
      const selectedOptions = options.filter(opt => selectedValues.includes(opt.value));
      
      const icon = <CheckBoxOutlineBlank fontSize="small" />;
      const checkedIcon = <CheckBox fontSize="small" />;

      return (
        <Autocomplete
          multiple
          options={options}
          disableCloseOnSelect
          value={selectedOptions}
          onChange={(_, newValue) => {
            onChange(newValue.map((v) => v.value));
          }}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, val) => option.value === val.value}
          size="small"
          renderOption={(props, option, { selected }) => (
            <li {...props} style={{ gap: 8 }}>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                checked={selected}
              />
              {option.label}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              required={required && selectedOptions.length === 0}
            />
          )}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  label={option.label}
                  {...tagProps}
                  size="small"
                />
              );
            })
          }
        />
      );
    }

    // Regular text input
    return (
      <TextField
        fullWidth
        label={label}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        type={type}
        required={required}
        multiline={multiline}
        rows={rows}
        InputLabelProps={type === 'date' ? { shrink: true } : undefined}
        size="small"
      />
    );
  }

  // Read mode display
  const displayText = displayValue || (Array.isArray(value) ? value.join(', ') : value) || '-';

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
        {icon}
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          {label}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
        {displayText}
      </Typography>
    </Box>
  );
};

export default EditableField;
